import {
  formatPatientName,
  normalizeBoolean,
  normalizeCPF,
  normalizePhoneBR,
  normalizeSex,
  parseDateFlexible,
  type PatientImportRecord,
  type PatientNameFormat,
} from "@bookdental/schemas";
import type { ParsedFile } from "./parseFile";

type BuildOptions = {
  mappings: Record<string, string>;
  nameFormat: PatientNameFormat;
  addressColumns: string[];
};

/**
 * Converte as linhas parseadas em PatientImportRecord[] (ja normalizado).
 * Usa o mesmo pipeline que o backend do apolonia_v2 — adaptado pra Prisma (digitos).
 */
export function buildPatientRecords(
  parsed: ParsedFile,
  opts: BuildOptions,
): PatientImportRecord[] {
  const { mappings, nameFormat, addressColumns } = opts;

  const colToField: Array<[number, string]> = [];
  parsed.headers.forEach((h, idx) => {
    const field = mappings[h];
    if (field && field !== "ignore") colToField.push([idx, field]);
  });

  const addressIndices: number[] = [];
  for (const col of addressColumns) {
    const idx = parsed.headers.indexOf(col);
    if (idx !== -1) addressIndices.push(idx);
  }

  const out: PatientImportRecord[] = [];

  for (const row of parsed.rows) {
    const rec: Partial<PatientImportRecord> = {};

    for (const [colIdx, field] of colToField) {
      const raw = row[colIdx] ?? "";
      if (!raw) continue;

      switch (field) {
        case "name":
          rec.name = formatPatientName(raw, nameFormat);
          break;
        case "email":
          rec.email = raw.toLowerCase();
          break;
        case "phone": {
          const p = normalizePhoneBR(raw);
          if (p) rec.phone = p;
          break;
        }
        case "landline": {
          const p = normalizePhoneBR(raw);
          if (p) rec.landline = p;
          break;
        }
        case "birthDate": {
          const d = parseDateFlexible(raw);
          if (d) rec.birthDate = d.toISOString().slice(0, 10);
          break;
        }
        case "document": {
          const c = normalizeCPF(raw);
          if (c) rec.document = c;
          break;
        }
        case "sex": {
          const s = normalizeSex(raw);
          if (s) rec.sex = s;
          break;
        }
        case "active":
          rec.active = normalizeBoolean(raw);
          break;
        case "state":
          rec.state = raw.toUpperCase().slice(0, 2);
          break;
        case "zipCode": {
          const digits = raw.replace(/\D/g, "");
          if (digits) rec.zipCode = digits.slice(0, 8);
          break;
        }
        default:
          (rec as Record<string, unknown>)[field] = raw;
      }
    }

    if (addressIndices.length > 0 && !rec.address) {
      const parts = addressIndices
        .map((idx) => row[idx]?.trim() ?? "")
        .filter((v) => v !== "");
      if (parts.length > 0) rec.address = parts.join(", ");
    }

    if (!rec.name) continue;
    out.push(rec as PatientImportRecord);
  }

  return out;
}
